import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import InteractiveBlock from "./Utils/InteractiveBlock";
import { gsap } from "gsap";

const Sidebar = ({
  currentPage,
  setCurrentPage,
  isFormFocused,
  isButtonHovered,
  isMessageFocused,
}) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("hello");
  const [lineStyle, setLineStyle] = useState({ top: 10, opacity: 0 });
  const navLinksRef = useRef({});

  // Mobile responsiveness state
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      // Close menu if resizing to desktop
      if (!mobile) setIsMenuOpen(false);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Toggle mobile menu
  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    // Only observe sections when on portfolio page
    if (currentPage !== "portfolio") return;

    const sections = document.querySelectorAll("section[id]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-50% 0px -50% 0px",
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, [currentPage]);

  useEffect(() => {
    // Only handle scroll when on portfolio page
    if (currentPage !== "portfolio") return;

    const handleScroll = () => {
      const activeNavItem = navLinksRef.current[activeSection];
      if (!activeNavItem) return;

      const section = document.getElementById(activeSection);
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const windowHeight = window.innerHeight;

      let progress = 0;

      // Spezielle Logik für erste und letzte Section für perfekten Übergang
      let start, end;

      // Spezielle Logik für erste und letzte Section für perfekten Übergang
      if (activeSection === "hello") {
        start = 40; // Margin-top
        end = windowHeight / 2 - sectionHeight;
      } else if (activeSection === "contact") {
        start = 40;
        end = windowHeight - sectionHeight;
      } else {
        // Alle anderen Sections: Enden wenn die nächste Section den Trigger-Punkt erreicht
        // Trigger-Punkt ist jetzt bei 10% der Bildschirmhöhe (wegen rootMargin -90%)
        // Das sorgt dafür, dass der Würfel erst ganz unten ankommt, wenn die Section fast weg ist
        start = 40;
        const triggerPoint = windowHeight * 0.1;
        end = triggerPoint - sectionHeight;
      }

      progress = (start - sectionTop) / (start - end);

      // Begrenze auf 0 bis 1
      progress = Math.max(0, Math.min(1, progress));

      // Hole die Höhe des aktiven Menü-Items
      const navItemHeight = activeNavItem.offsetHeight;

      // Berechne die Position des Strichs innerhalb des Menü-Items
      // Von 14px bis (navItemHeight - 10px - 14px) (14px ist die Höhe des Würfels)
      const minPos = 13;
      const maxPos = navItemHeight - 33;
      const position = minPos + progress * (maxPos - minPos);

      // Opacity immer 1, da der Würfel nicht mehr ausgeblendet werden soll
      const opacity = 1;

      setLineStyle({ top: position, opacity });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial berechnen

    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection, currentPage]);

  // GSAP animation for sidebar head - different speeds for enter vs exit
  const prevSectionRef = useRef(activeSection);

  useEffect(() => {
    if (currentPage !== "portfolio") return;

    const sidebarHead = document.getElementById("sidebar-head");
    if (!sidebarHead) return;

    const isEntering =
      activeSection === "contact" && prevSectionRef.current !== "contact";
    const isExiting =
      activeSection !== "contact" && prevSectionRef.current === "contact";

    if (isEntering) {
      // Fast bouncy entrance when scrolling down into contact
      gsap.to(sidebarHead, {
        x: "-50%",
        opacity: 1,
        duration: 0.8,
        ease: "back.out(1.4)",
      });
    } else if (isExiting) {
      // Slow exit when scrolling up away from contact
      gsap.to(sidebarHead, {
        x: "-150%",
        opacity: 0,
        duration: 1.5,
        ease: "power2.inOut",
      });
    }

    prevSectionRef.current = activeSection;
  }, [activeSection, currentPage]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    setCurrentPage("portfolio");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (e) => {
    e.preventDefault();

    // Close mobile menu if open
    setIsMenuOpen(false);

    // Dispatch event to close any open overlay
    window.dispatchEvent(new CustomEvent("closeOverlay"));

    const targetId = e.currentTarget.getAttribute("href").substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const offset = isMobile ? 80 : 40;
      const elementPosition =
        targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <nav className={`sidebar ${isMenuOpen ? "menu-open" : ""}`}>
      <div className="top-area">
        <div
          className="logo"
          onClick={handleLogoClick}
          style={{ cursor: "pointer" }}
        >
          <Logo />
        </div>

        {/* Hamburger button for mobile */}
        <button
          className={`hamburger ${isMenuOpen ? "open" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        <div
          className={`nav-links ${isMenuOpen ? "open" : ""}`}
          style={!isMobile ? { position: "relative" } : {}}
        >
          <a
            href="#hello"
            onClick={handleNavClick}
            className={
              activeSection === "hello" && currentPage === "portfolio"
                ? "active"
                : ""
            }
            ref={(el) => (navLinksRef.current["hello"] = el)}
          >
            <span className="nav-text">
              <span className="num">01</span>
              <span className="spacer"></span>
              <span className="nav-label">{t("nav.intro")}</span>
            </span>
            {activeSection === "hello" && currentPage === "portfolio" && (
              <span
                className="nav-line"
                style={{
                  top: `${lineStyle.top}px`,
                  opacity: lineStyle.opacity,
                }}
              ></span>
            )}
          </a>
          <a
            href="#stack"
            onClick={handleNavClick}
            className={
              activeSection === "stack" && currentPage === "portfolio"
                ? "active"
                : ""
            }
            ref={(el) => (navLinksRef.current["stack"] = el)}
          >
            <span className="nav-text">
              <span className="num">02</span>
              <span className="spacer"></span>
              <span className="nav-label">{t("nav.stack")}</span>
            </span>
            {activeSection === "stack" && currentPage === "portfolio" && (
              <span
                className="nav-line"
                style={{
                  top: `${lineStyle.top}px`,
                  opacity: lineStyle.opacity,
                }}
              ></span>
            )}
          </a>
          <a
            href="#work"
            onClick={handleNavClick}
            className={
              activeSection === "work" && currentPage === "portfolio"
                ? "active"
                : ""
            }
            ref={(el) => (navLinksRef.current["work"] = el)}
          >
            <span className="nav-text">
              <span className="num">03</span>
              <span className="spacer"></span>
              <span className="nav-label">{t("nav.work")}</span>
            </span>
            {activeSection === "work" && currentPage === "portfolio" && (
              <span
                className="nav-line"
                style={{
                  top: `${lineStyle.top}px`,
                  opacity: lineStyle.opacity,
                }}
              ></span>
            )}
          </a>
          <a
            href="#team"
            onClick={handleNavClick}
            className={
              activeSection === "team" && currentPage === "portfolio"
                ? "active"
                : ""
            }
            ref={(el) => (navLinksRef.current["team"] = el)}
          >
            <span className="nav-text">
              <span className="num">04</span>
              <span className="spacer"></span>
              <span className="nav-label">{t("nav.team")}</span>
            </span>
            {activeSection === "team" && currentPage === "portfolio" && (
              <span
                className="nav-line"
                style={{
                  top: `${lineStyle.top}px`,
                  opacity: lineStyle.opacity,
                }}
              ></span>
            )}
          </a>
          <a
            href="#contact"
            onClick={handleNavClick}
            className={
              activeSection === "contact" && currentPage === "portfolio"
                ? "active"
                : ""
            }
            ref={(el) => (navLinksRef.current["contact"] = el)}
          >
            <span className="nav-text">
              <span className="num">05</span>
              <span className="spacer"></span>
              <span className="nav-label">{t("nav.contact")}</span>
            </span>
            {activeSection === "contact" && currentPage === "portfolio" && (
              <span
                className="nav-line"
                style={{
                  top: `${lineStyle.top}px`,
                  opacity: lineStyle.opacity,
                }}
              ></span>
            )}
          </a>

          {/* Secondary Interactive Head - Moved inside nav-links for responsive positioning */}
          {/* Hidden on mobile - resource-heavy 3D rendering not needed for touch devices */}
          {!isMobile && (
            <InteractiveBlock
              id="sidebar-head"
              style={{
                position: "absolute",
                top: "100%", // Push it below the nav links
                marginTop: "-80px", // Pull it up slightly to overlap/sit nicely
                left: "50%", // Center horizontally relative to nav-links
                // Initial state: hidden off to the left (GSAP will animate)
                transform: "translate(-150%, 0)",
                width: "400px",
                height: "400px",
                zIndex: 10,
                opacity: 0,
                // No CSS transition - GSAP handles animation with different speeds
                pointerEvents: activeSection === "contact" ? "auto" : "none",
                right: "auto",
              }}
              isShy={isFormFocused && !isMessageFocused}
              isExcited={isButtonHovered}
              isPeeking={isMessageFocused}
            />
          )}
        </div>
      </div>

      <div className="sidebar-footer">
        <span className="footer-location">
          {t("footer.location")}
          <LanguageSwitcher />
        </span>
        <br />
        &copy; 2025
      </div>
    </nav>
  );
};

export default Sidebar;
